import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { DialogCardComponent } from '../../../shared/components/ui/dialog-card/dialog-card.component';

export interface DialogButton {
  label: string;
  value: string;
  variant?: 'flat' | 'stroked' | 'basic';
  class?: string;
}

export interface DialogConfig {
  title: string;
  message?: string | string[];
  image?: string;
  buttons?: DialogButton[];
}

@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly dialog = inject(MatDialog);

  /**
   * Abre un diálogo.
   * @param config - Configuración del diálogo (title, message, image, buttons)
   * @param width - Ancho del diálogo (por defecto 320px)
   * @param disableClose - Previene cerrar con ESC o click fuera (por defecto false)
   * @returns Observable con el value del botón clickeado o undefined
   */
  open$(
    config: DialogConfig,
    width = '320px',
    disableClose = false,
  ): Observable<string | undefined> {
    const buttons = config.buttons ?? [{ label: 'Aceptar', value: 'confirm' }];

    return this.dialog
      .open(DialogCardComponent, {
        data: { ...config, buttons },
        width,
        disableClose,
        panelClass: ['custom-dialog'],
      })
      .afterClosed();
  }

  /**
   * Abre un diálogo de alerta con un botón.
   * @param title - Título del diálogo
   * @param message - Mensaje del diálogo (string o array de strings para múltiples párrafos)
   * @param image - Imagen a mostrar (opcional)
   * @returns Observable con el resultado
   */
  alert$(
    title: string,
    message?: string | string[],
    image?: string,
  ): Observable<string | undefined> {
    return this.open$({ title, message, image }, '320px', true);
  }

  // --------------- Alertas centralizadas --------------- //

  noConnection$(): Observable<string | undefined> {
    return this.alert$(
      'Sin conexión',
      'Verifica tu conexión a internet e intenta nuevamente.',
      'no-internet',
    );
  }

  sessionExpired$(message?: string): Observable<string | undefined> {
    return this.open$({
      title: 'Sesión expirada',
      message: message || 'Tu sesión ha finalizado porque no había actividad. Intenta iniciar sesión nuevamente.',
      image: 'no-message',
      buttons: [
        { label: 'Reintentar', value: 'retry', variant: 'flat', class: 'w-full' },
      ],
    },
      '320px', true)
  }

  accountLocked$(message?: string): Observable<string | undefined> {
    return this.alert$(
      'Cuenta bloqueada',
      message || 'Tu cuenta ha sido bloqueada temporalmente. Intenta nuevamente más tarde o contacta a soporte.',
      'no-message',
    );
  }

  tooManyAttempts$(message?: string): Observable<string | undefined> {
    return this.alert$(
      'Demasiados intentos',
      message || 'Has superado el número máximo de intentos. Por seguridad, tu sesión ha sido cerrada.',
      'no-message',
    );
  }

  accessDenied$(message?: string): Observable<string | undefined> {
    return this.alert$(
      'Acceso denegado',
      message || 'No tenés permisos para realizar esta acción. Si el problema persiste, contacta a soporte.',
      'no-message',
    );
  }

  serverError$(): Observable<string | undefined> {
    return this.alert$(
      'Error inesperado',
      'Ocurrió un error interno. Por favor, intenta nuevamente más tarde.',
      'no-connection',
    );
  }

  gatewayError$(): Observable<string | undefined> {
    return this.alert$(
      'Servicio no disponible',
      'Estamos experimentando problemas de comunicación. Por favor, intenta nuevamente más tarde.',
      'no-connection',
    );
  }

  serviceUnavailable$(): Observable<string | undefined> {
    return this.alert$(
      'Servicio no disponible',
      'Estamos experimentando problemas técnicos. Por favor, intenta nuevamente más tarde.',
      'no-connection',
    );
  }
}
